// The lockers' worn steel (Grey, 2026-10-24: "make the texture and material for the lockers much more realistic and gritty and
// textured"): the maps from tools/locker_textures.py on the lockers' own materials (ships/build_locker_kit.py: Locker_*). The plain
// parts (the body, the shelves, the floor, the dark trim, the rubber) get UVs laid in metres, a box projection by each face's way, and
// the full set (painted gunmetal, chipped, scratched and streaked with grime; its relief; glossy where worn, rough where dirty); the
// parts with a pattern of their own (the perforated door leaves, the rails) keep it and get the grime as Babylon's detail layer.
// Run once on the ship's meshes, before the lockers' templates are copied from them (client/lockers.ts), so printed lockers match
import { PBRMaterial, Texture, Vector3, Quaternion, VertexBuffer, type AbstractMesh, type Mesh, type Scene } from "@babylonjs/core";

const V = 1;                                              // bump with a new bake of the maps (browsers fetch them again)
const TILE = 1.2;                                         // metres one tile of the maps covers
const BOXED: Record<string, { rough?: number; metal?: number; bump?: number; maps?: boolean }> = {
  Locker_Steel: {}, Locker_Shelf: {}, Locker_Floor: { bump: 0.7 }, Locker_Dark: {}, Locker_Rubber: { maps: false, bump: 0.5 },
};
const DETAILED = new Set(["Locker_Leaf_Perf", "Locker_Rail"]);
const PATH = "/assets/env/";

export function lockerGrit(scene: Scene, meshes: AbstractMesh[]): number {
  const tex = (f: string, linear = false) => { const t = new Texture(`${PATH}${f}?v=${V}`, scene, false, true, Texture.TRILINEAR_SAMPLINGMODE); if (linear) t.gammaSpace = false; t.anisotropicFilteringLevel = 8; return t; };
  let alb: Texture | null = null, nrm: Texture | null = null, orm: Texture | null = null, det: Texture | null = null;
  const done = new Set<PBRMaterial>(); let n = 0;
  for (const m of meshes) {
    const mat = m.material; if (!(mat instanceof PBRMaterial)) continue;
    const boxed = BOXED[mat.name], detailed = DETAILED.has(mat.name); if (!boxed && !detailed) continue;
    if (boxed && boxUV(m as Mesh)) n++;
    if (done.has(mat)) continue; done.add(mat);
    if (boxed) {
      alb ??= tex("locker_albedo.jpg"); nrm ??= tex("locker_normal.jpg", true); orm ??= tex("locker_orm.jpg", true);
      const c = mat.albedoColor; mat.albedoColor = c.scale(1 / 0.6);   // the maps' grey is about 0.6: the material's own colour kept on average
      mat.albedoTexture = alb;
      mat.bumpTexture = nrm; mat.bumpTexture.level = boxed.bump ?? 1;
      if (boxed.maps !== false) {
        mat.metallicTexture = orm; mat.useRoughnessFromMetallicTextureGreen = true; mat.useMetallnessFromMetallicTextureBlue = true; mat.useAmbientOcclusionFromMetallicTextureRed = false;
        mat.metallic = boxed.metal ?? 1; mat.roughness = boxed.rough ?? 1;    // (the map's values as they are)
      }
    } else {
      det ??= tex("locker_detail.png", true); det.uScale = det.vScale = 3;
      mat.detailMap.texture = det; mat.detailMap.isEnabled = true; mat.detailMap.diffuseBlendLevel = 0.75; mat.detailMap.roughnessBlendLevel = 0.8; mat.detailMap.bumpLevel = 1;
    }
  }
  return n;
}
// UVs in metres, each vertex by its face's way (the axis its normal leans along most): x faces take (z, y), y faces (x, z), z faces (x, y)
function boxUV(m: Mesh): boolean {
  const pos = m.getVerticesData(VertexBuffer.PositionKind), nor = m.getVerticesData(VertexBuffer.NormalKind); if (!pos || !nor) return false;
  const s = new Vector3(), q = new Quaternion(), t = new Vector3(); m.computeWorldMatrix(true).decompose(s, q, t);
  const sx = Math.abs(s.x) / TILE, sy = Math.abs(s.y) / TILE, sz = Math.abs(s.z) / TILE, uv = new Float32Array((pos.length / 3) * 2);
  for (let i = 0, j = 0; i < pos.length; i += 3, j += 2) {
    const ax = Math.abs(nor[i]), ay = Math.abs(nor[i + 1]), az = Math.abs(nor[i + 2]), x = pos[i] * sx, y = pos[i + 1] * sy, z = pos[i + 2] * sz;
    if (ax >= ay && ax >= az) { uv[j] = z; uv[j + 1] = y; } else if (ay >= az) { uv[j] = x; uv[j + 1] = z; } else { uv[j] = x; uv[j + 1] = y; }
  }
  m.setVerticesData(VertexBuffer.UVKind, uv, false); return true;
}
